import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RefreshCw, CheckCircle, AlertTriangle, XCircle, Activity, Clock, MessageSquare, ShoppingCart, Server } from 'lucide-react';

interface CronHealth {
  job_name: string;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_status: string;
  error_message: string | null;
  runs_24h: number;
  success_24h: number;
  errors_24h: number;
  health: 'healthy' | 'warning' | 'critical';
}

interface FailedCron {
  id: string;
  job_name: string;
  started_at: string;
  finished_at: string | null;
  error_message: string | null;
}

interface SystemSnapshot {
  generated_at: string;
  cron: {
    failed_jobs_24h: number;
    jobs_with_errors_24h: number;
  };
  chat: {
    pending_requests: number;
  };
  business: {
    orders_pending: number;
    messages_24h: number;
  };
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Jamais';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'À l\'instant';
  if (minutes < 60) return `Il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `Il y a ${days}j`;
}

function HealthBadge({ health }: { health: string }) {
  switch (health) {
    case 'healthy':
      return <Badge className="bg-green-100 text-green-800 border-green-300"><CheckCircle className="h-3 w-3 mr-1" />OK</Badge>;
    case 'warning':
      return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300"><AlertTriangle className="h-3 w-3 mr-1" />Warning</Badge>;
    case 'critical':
      return <Badge className="bg-red-100 text-red-800 border-red-300"><XCircle className="h-3 w-3 mr-1" />Critique</Badge>;
    default:
      return <Badge variant="outline">{health}</Badge>;
  }
}

export function MonitoringPanel() {
  const [cronHealth, setCronHealth] = useState<CronHealth[]>([]);
  const [failedCrons, setFailedCrons] = useState<FailedCron[]>([]);
  const [snapshot, setSnapshot] = useState<SystemSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [siteStatus, setSiteStatus] = useState<'up' | 'down' | 'checking'>('checking');
  const [siteResponseTime, setSiteResponseTime] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setSiteStatus('checking');

    // Check site availability
    try {
      const start = Date.now();
      const response = await fetch('https://nazandoo.com', { method: 'HEAD', mode: 'no-cors' });
      setSiteResponseTime(Date.now() - start);
      setSiteStatus('up');
    } catch {
      setSiteStatus('down');
      setSiteResponseTime(null);
    }

    // Fetch cron health view
    const { data: cronData } = await supabase
      .from('monitoring_cron_health' as any)
      .select('*');
    if (cronData) setCronHealth(cronData as any);

    // Fetch failed crons 24h
    const { data: failedData } = await supabase
      .from('monitoring_failed_cron_24h' as any)
      .select('*');
    if (failedData) setFailedCrons(failedData as any);

    // Fetch snapshot
    const { data: snapData } = await (supabase.rpc as any)('monitoring_system_snapshot');
    if (snapData) setSnapshot(snapData as SystemSnapshot);

    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const healthyCronCount = cronHealth.filter(c => c.health === 'healthy').length;
  const warningCronCount = cronHealth.filter(c => c.health === 'warning').length;
  const criticalCronCount = cronHealth.filter(c => c.health === 'critical').length;

  const globalHealth = criticalCronCount > 0 || siteStatus === 'down'
    ? 'critical'
    : warningCronCount > 0
      ? 'warning'
      : 'healthy';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Monitoring Production
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Dernière mise à jour : {lastRefresh.toLocaleTimeString('fr-FR')} — Auto-refresh 60s
          </p>
        </div>
        <Button onClick={fetchData} disabled={loading} variant="outline" size="sm">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Global Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Site Status */}
        <Card className={siteStatus === 'up' ? 'border-green-200' : siteStatus === 'down' ? 'border-red-200' : 'border-gray-200'}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Server className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Site Web</span>
            </div>
            <div className="flex items-center gap-2">
              {siteStatus === 'up' && <Badge className="bg-green-100 text-green-800">En ligne</Badge>}
              {siteStatus === 'down' && <Badge className="bg-red-100 text-red-800">Hors ligne</Badge>}
              {siteStatus === 'checking' && <Badge variant="outline">Vérification...</Badge>}
              {siteResponseTime !== null && (
                <span className="text-xs text-muted-foreground">{siteResponseTime}ms</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cron Jobs */}
        <Card className={globalHealth === 'healthy' ? 'border-green-200' : globalHealth === 'warning' ? 'border-yellow-200' : 'border-red-200'}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Cron Jobs</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-green-100 text-green-800">{healthyCronCount} OK</Badge>
              {warningCronCount > 0 && <Badge className="bg-yellow-100 text-yellow-800">{warningCronCount} warn</Badge>}
              {criticalCronCount > 0 && <Badge className="bg-red-100 text-red-800">{criticalCronCount} crit</Badge>}
            </div>
          </CardContent>
        </Card>

        {/* Pending Orders */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Commandes en attente</span>
            </div>
            <span className="text-2xl font-bold">{snapshot?.business?.orders_pending ?? '—'}</span>
          </CardContent>
        </Card>

        {/* Chat & Messages */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Messages (24h)</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold">{snapshot?.business?.messages_24h ?? '—'}</span>
              {(snapshot?.chat?.pending_requests ?? 0) > 0 && (
                <Badge className="bg-orange-100 text-orange-800">
                  {snapshot?.chat?.pending_requests} chat en attente
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Failed Crons Alert */}
      {failedCrons.length > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-800 flex items-center gap-2 text-base">
              <XCircle className="h-5 w-5" />
              Erreurs Cron (dernières 24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Heure</TableHead>
                  <TableHead>Erreur</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedCrons.map((fc) => (
                  <TableRow key={fc.id}>
                    <TableCell className="font-mono text-sm">{fc.job_name}</TableCell>
                    <TableCell className="text-sm">{timeAgo(fc.started_at)}</TableCell>
                    <TableCell className="text-sm text-red-700 max-w-md truncate">{fc.error_message || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Cron Health Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Santé des Cron Jobs</CardTitle>
          <CardDescription>Statut et historique des tâches planifiées</CardDescription>
        </CardHeader>
        <CardContent>
          {cronHealth.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Aucune donnée cron trouvée. Vérifiez que la vue <code>monitoring_cron_health</code> a été créée dans Supabase.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Dernière exécution</TableHead>
                  <TableHead className="text-center">Succès (24h)</TableHead>
                  <TableHead className="text-center">Erreurs (24h)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cronHealth.map((job) => (
                  <TableRow key={job.job_name}>
                    <TableCell className="font-mono text-sm">{job.job_name}</TableCell>
                    <TableCell><HealthBadge health={job.health} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{timeAgo(job.last_started_at)}</TableCell>
                    <TableCell className="text-center text-sm text-green-700">{job.success_24h}</TableCell>
                    <TableCell className="text-center text-sm">
                      {job.errors_24h > 0 ? (
                        <span className="text-red-700 font-medium">{job.errors_24h}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
